import axios, { AxiosError } from 'axios';
import { trainingManagementApi } from '../api/trainingManagementApi';

// Re-export interfaces for convenience (or define more abstract ones if needed)
// TODO: Import or define actual interfaces matching the API layer
export interface Configuration { 
    _id?: string;
    max_concurrent_runs?: number;
    available_gpu_ids?: string[];
    supported_models?: string[];
    default_parameters?: {
        yolov8n?: any; 
        yolov11n?: any;
        darkyolov4_tiny?: any;
    };
    updated_at?: string | Date;
}
export interface Dataset { 
    _id: string; 
    name: string; 
    description?: string;
    status?: string;
    statistics?: { annotation_count?: number; background_count?: number };
    storage_path?: string;
    created_at?: string | Date;
    updated_at?: string | Date;
}
export interface TrainingRun { 
    _id: string;
    run_number?: number;
    status?: string; 
    model_type?: string; 
    dataset_id?: string; 
    parameters?: any; 
    progress?: {
        current_epoch?: number | null;
        total_epochs?: number | null;
        current_iteration?: number | null;
        total_iterations?: number | null;
        current_loss?: number | null;
        last_update_time?: string | Date;
        metrics?: any; 
    };
    assigned_gpu_id?: string;
    start_time?: string | Date; 
    end_time?: string | Date; 
    estimated_remaining_time_seconds?: number;
    error_message?: string;
    created_at?: string | Date;
    updated_at?: string | Date;
}

// --- Type for the new /account-camera-info endpoint response --- 
export interface CameraInfo { 
    _id: string;
    displayName: string;
    path: string; // This should be the AccountName/CameraName format
}
export interface SourceGroup {
    accountId: string;
    accountName: string;
    cameras: CameraInfo[];
}

// Helper function to extract a useful error message
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    if (axiosError.response) {
      // Backend responded with an error status code
      const responseData = axiosError.response.data as any; // Type assertion
      const backendMessage = responseData?.message || responseData?.error || JSON.stringify(responseData);
      return `Serverfehler ${axiosError.response.status}: ${backendMessage}`;
    } else if (axiosError.request) {
      // Request was made but no response received (network error, server down)
      return 'Netzwerkfehler: Keine Antwort vom Server erhalten.';
    } else {
      // Error setting up the request
      return `Fehler bei der Anfrage-Erstellung: ${axiosError.message}`;
    }
  } else if (error instanceof Error) {
    return error.message;
  } else {
    return 'Ein unbekannter Fehler ist aufgetreten.';
  }
};

export const trainingManagementService = {
  async getConfiguration(): Promise<Configuration> {
    try {
      return await trainingManagementApi.getConfiguration();
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error fetching configuration:", error);
      throw new Error(message);
    }
  },

  async startNewTrainingRun(modelType: string, datasetId: string, parameters: any): Promise<TrainingRun> {
    try {
      return await trainingManagementApi.startNewTrainingRun(modelType, datasetId, parameters);
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error starting training run:", error);
      throw new Error(message);
    }
  },

  async requestStopTrainingRun(runId: string): Promise<{ message: string }> {
    try {
      return await trainingManagementApi.requestStopTrainingRun(runId);
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error stopping training run:", error);
      throw new Error(message);
    }
  },

  async getDatasets(): Promise<Dataset[]> {
    try {
      return await trainingManagementApi.getDatasets();
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error fetching datasets:", error);
      throw new Error(message);
    }
  },

  async getAccountCameraInfo(): Promise<SourceGroup[]> {
    try {
      return await trainingManagementApi.getAccountCameraInfo();
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error fetching account camera info:", error);
      throw new Error(message);
    }
  },

  async createDataset(name: string, sources: string[]): Promise<{ message: string; datasetId: string; status: string }> {
    try {
      return await trainingManagementApi.createDataset(name, sources);
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error creating dataset:", error);
      throw new Error(message);
    }
  },

  async getActiveTrainingRuns(): Promise<TrainingRun[]> {
    try {
      return await trainingManagementApi.getActiveTrainingRuns();
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error fetching active training runs:", error);
      throw new Error(message);
    }
  },
  
  async getTrainingRunHistory(limit: number = 50, skip: number = 0): Promise<TrainingRun[]> {
    try {
      return await trainingManagementApi.getTrainingRunHistory(limit, skip);
    } catch (error) {
      const message = getErrorMessage(error);
      console.error("Service Error fetching training run history:", error);
      throw new Error(message);
    }
  }
}; 